/**
 * The rights of Beheer › Personeel & rechten, as the prototype names them, on top of the server's
 * permissions. The server decides; these rules only mirror it so the screen shows the right state:
 * deleting, the menu and the terms are the owner's alone, cancelling is for managers and the owner
 * (besluiten 30/09) and cannot be given as an exception.
 */
import type { PaymentMethod, Permission, StaffEntry, StaffRole } from "../../api/types";

export interface Right {
  id: string;
  name: string;
  /** Asked with a personal code on the till. */
  code: boolean;
  permissions: Permission[];
}

export const RIGHTS: Right[] = [
  { id: "order", name: "Bestellen & afrekenen", code: false, permissions: ["orders.create", "orders.pay"] },
  { id: "cancel", name: "Annuleren / terugbetalen", code: true, permissions: ["orders.cancel"] },
  { id: "discount", name: "Korting / gratis", code: true, permissions: ["discount"] },
  {
    id: "edit",
    name: "Betaalwijze wijzigen / betaalde bestelling bewerken",
    code: true,
    permissions: ["orders.edit"],
  },
  { id: "soldout", name: "Uitverkocht", code: false, permissions: ["menu.soldout"] },
  { id: "staff", name: "Personeel beheren", code: false, permissions: ["staff.manage"] },
  { id: "reports", name: "Rapporten bekijken", code: false, permissions: ["reports.view"] },
  // the day closing apart from looking at the figures (Furkan, 11/10/2026)
  { id: "zclose", name: "Dagafsluiting (Z)", code: false, permissions: ["reports.close"] },
  {
    id: "admin",
    name: "Menu, acties, printers, zones, klanten, algemeen",
    code: false,
    permissions: ["menu.edit", "settings.edit"],
  },
  { id: "courier", name: "Koerier-app (ritten)", code: false, permissions: ["courier.app"] },
  { id: "settle", name: "Koeriers afrekenen (cash tellen)", code: true, permissions: ["couriers.settle"] },
];

export const ROLE_ORDER: StaffRole[] = ["owner", "manager", "staff", "waiter", "courier"];
/** A manager adds and changes these roles only (prototype). */
export const MANAGER_ROLES: StaffRole[] = ["staff", "waiter", "courier"];
/** Payment methods a person can be allowed to take (online orders are paid on the website). */
export const STAFF_PAYMENT_METHODS: PaymentMethod[] = ["cash", "bancontact", "card", "meal_voucher", "bank_qr"];

const OWNER_ONLY: Permission[] = ["orders.delete", "menu.edit", "terms.accept"];

export type Exceptions = Partial<Record<Permission, boolean>>;

/** "Korting / gratis" → "Korting", as on the pills of the prototype. */
export const shortName = (right: Right) => right.name.split(" /")[0]!.split(",")[0]!;

export const holds = (right: Right, can: readonly Permission[]) =>
  right.permissions.every((permission) => can.includes(permission));

/** Whether the owner may give or take this right from someone with this role. */
export function grantable(role: StaffRole, right: Right): boolean {
  if (role === "owner") return false;
  return right.permissions.every(
    (permission) => !OWNER_ONLY.includes(permission) && (permission !== "orders.cancel" || role === "manager"),
  );
}

/** The value of a right with these exceptions. */
export function rightValue(defaults: readonly Permission[], exceptions: Exceptions, right: Right): boolean {
  return right.permissions.every((permission) => exceptions[permission] ?? defaults.includes(permission));
}

export const isException = (exceptions: Exceptions, right: Right) =>
  right.permissions.some((permission) => exceptions[permission] !== undefined);

/** Ticks or unticks a right: only what differs from the role's default stays an exception. */
export function setRight(
  defaults: readonly Permission[],
  exceptions: Exceptions,
  right: Right,
  value: boolean,
): Exceptions {
  const touched = new Set<Permission>(right.permissions);
  const kept = Object.entries(exceptions).filter(([permission]) => !touched.has(permission as Permission));
  const changed = right.permissions
    .filter((permission) => value !== defaults.includes(permission))
    .map((permission) => [permission, value] as const);
  return Object.fromEntries([...kept, ...changed]);
}

/** The pills of a row: what the person may do (orange = exception) and what was taken (red ✕). */
export function pills(member: StaffEntry): { right: Right; kind: "normal" | "granted" | "removed" }[] {
  const has = RIGHTS.filter((right) => holds(right, member.can)).map((right) => ({
    right,
    kind: right.permissions.some((permission) => member.permissions[permission] === true)
      ? ("granted" as const)
      : ("normal" as const),
  }));
  const removed = RIGHTS.filter(
    (right) =>
      !holds(right, member.can) && right.permissions.some((permission) => member.permissions[permission] === false),
  ).map((right) => ({ right, kind: "removed" as const }));
  return [...has, ...removed];
}

/** Whether this editor may open this person (prototype: a manager not for managers and owners). */
export const mayEdit = (editor: StaffRole, member: { role: StaffRole }) =>
  editor === "owner" || MANAGER_ROLES.includes(member.role);
